/*
 * The navigation at the end of both headers: Articles, Release Notes, Manage, and
 * the account action. One component, so the public site and the admin site
 * cannot drift.
 *
 *   Articles   the public site - "Browse" until 2026-10-09, renamed at the owner's
 *              request. Highlighted on the home page, as it always was.
 *   Release Notes
 *              the public release-note list (S42, 2026-10-12: the owner placed it
 *              "between Articles and Manage", in both headers). Always there, and
 *              highlighted on the list and on every note's own page - no `end`.
 *              The admin shell links the PUBLIC list too, as Articles does; the
 *              admin list of release notes is in the admin sidebar.
 *   Manage     the admin site. In the admin shell always there, and highlighted
 *              there as the section the reader is in. In the public shell only
 *              when the server has confirmed the reader may administer
 *              (identity.isAdmin, from /api/admin/me - asked on a public page
 *              too, AuthProvider says how), so it appears a moment after the page.
 *              Since 2026-10-15 a page load that finds the sign-in expired renews
 *              it through Entra first: until then the next day's header kept
 *              "Sign out" and lost "Manage" (the owner's report from TEST).
 *   Sign in    a reader who is not signed in, on the public site. Starts the Entra
 *              sign-in and brings them back to the same page. The admin site has
 *              its own sign-in card, so its header offers none.
 *   Sign out   a reader who is signed in, on either site. Signs out of Entra and
 *              brings them back to the same page (auth/signOutReturn.ts).
 *
 * By the owner's requests of 2026-10-09: "Sign in" in place of "Manage" first, then
 * the name once signed in with Manage only for an administrator, then this - the
 * name moved to a "Signed in as" block in the sidebar (SignedInAs.tsx), "Sign out"
 * in its place, and the admin header given the same two links in place of "View
 * site" and the name.
 *
 * The account action sits the nav's own gap from the links, box to box, like the
 * links from each other (styles.css, .header-nav-button) - an extra margin
 * matching the light/dark switch's distance was tried first and trimmed away.
 * With a third link the nav may need more than one row on a phone: it wraps
 * BETWEEN items, never inside one - "Release Notes" is two words and must not
 * break inside its pill (styles.css, .header-nav).
 *
 * Every word here is the chosen language's (S48, 2026-10-29): "Artikler",
 * "Udgivelsesnoter", "Administrer", "Log ind" and "Log ud" in Danish. The longer
 * Danish words are why each link's reserved width is its own label's, not a
 * fixed one.
 *
 * While a tab is still restoring an earlier sign-in there is no account action
 * yet - neither a "Sign in" flashed at someone already in, nor a "Sign out" for a
 * session not yet confirmed - and where Entra is not configured there is none at
 * all. Until /api/config answers, sign-in is assumed available.
 */

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { RELEASE_NOTES_PATH } from '@accotool-kb/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { useMessages } from '../i18n/language.js';

export function SiteNav({ shell }: { shell: 'public' | 'admin' }): JSX.Element {
  const auth = useAuth();
  const t = useMessages();
  const [starting, setStarting] = useState(false);

  // A sign-in that could not start (the server's sign-in settings failed to load)
  // gives the button back rather than leaving it saying "Signing in…".
  useEffect(() => {
    if (auth.configError) setStarting(false);
  }, [auth.configError]);

  const showManage = shell === 'admin' || auth.identity?.isAdmin === true;
  const accountAction =
    auth.config?.auth.configured === false || auth.restoringSession
      ? null
      : auth.signedIn
        ? 'sign-out'
        : shell === 'public'
          ? 'sign-in'
          : null;

  return (
    <nav className="header-nav" aria-label={t.nav.label}>
      {/* data-label: the bold width each link reserves, so moving the highlight
          moves nothing (styles.css, .header-nav a) - in the words shown. */}
      <NavLink to="/" end data-label={t.nav.articles}>
        {t.nav.articles}
      </NavLink>
      <NavLink to={RELEASE_NOTES_PATH} data-label={t.nav.releaseNotes}>
        {t.nav.releaseNotes}
      </NavLink>
      {showManage && (
        <NavLink to="/admin" data-label={t.nav.manage}>
          {t.nav.manage}
        </NavLink>
      )}

      {accountAction === 'sign-in' && (
        <button
          type="button"
          className="header-nav-button"
          disabled={starting}
          onClick={() => {
            setStarting(true);
            auth.signIn();
          }}
        >
          {starting ? t.nav.signingIn : t.nav.signIn}
        </button>
      )}

      {accountAction === 'sign-out' && (
        <button type="button" className="header-nav-button" onClick={auth.signOut}>
          {t.nav.signOut}
        </button>
      )}
    </nav>
  );
}
